import { useRouter } from 'next/router';
import { memo, type ReactNode, useCallback } from 'react';

import { NotificationsHeader, type NotificationsProps } from '@air/component-notifications';
import { cn } from '@air/utils-tailwind';

import { AirNotificationList } from '~/components/AirNotifications/components/AirNotificationList';
import { AnnouncementsNewsList } from '~/components/AirNotifications/components/AnnouncementsNewsList';
import { useMarkAllNotificationsAsRead } from '~/components/AirNotifications/hooks/useMarkAllNotificationsAsRead';
import { useNotificationsPanelTab } from '~/components/AirNotifications/hooks/useNotificationsPanelTab';
import { Routes } from '~/constants/routes';

export type DesktopNotificationsProps = Pick<NotificationsProps, 'className'> & {
  headerLeading?: ReactNode;
  onClose?: () => void;
  isUI2?: boolean;
};

export const DesktopNotifications = memo(({ className, headerLeading, onClose, isUI2 }: DesktopNotificationsProps) => {
  const router = useRouter();
  const { markAllNotificationsAsRead } = useMarkAllNotificationsAsRead();
  const { filters, isNewsTab, notificationFilter, onFilterClick } = useNotificationsPanelTab();

  const onSettingsClick = useCallback(() => {
    onClose?.();
    router.push(Routes.account.notificationSettings);
  }, [onClose, router]);

  return (
    <div className={cn('flex h-full flex-col', className)} data-testid="NOTIFICATIONS">
      <NotificationsHeader
        leading={headerLeading}
        filters={filters}
        onFilterClick={onFilterClick}
        onMarkAllAsRead={markAllNotificationsAsRead}
        onSettingsClick={onSettingsClick}
        isUI2={isUI2}
      />
      {isNewsTab ? (
        <AnnouncementsNewsList />
      ) : (
        <AirNotificationList filter={notificationFilter} onNotificationClick={onClose} />
      )}
    </div>
  );
});

DesktopNotifications.displayName = 'DesktopNotifications';
